import type { Idioma } from "@/content/catalogo";

export const idiomas: Idioma[] = ["es", "en"];
export const esIdioma = (v: string): v is Idioma => (idiomas as string[]).includes(v);

const es = {
  meta: {
    titulo: "Mazamitla Sierra Tours · Tours en camión safari, a caballo y nocturnos",
    descripcion: "Reserva en línea tours en Mazamitla, Jalisco: Sierra del Tigre en camión safari, cascadas, paseos a caballo, tour nocturno y helicóptero.",
  },
  nav: { tours: "Tours", cabanas: "Cabañas", bar: "Bar", ayuda: "Ayuda", reservar: "Reservar", idioma: "English", menu: "Menú" },
  demo: "Mazamitla Sierra Tours · Planea tu visita",
  inicio: {
    h1: "Vive la Sierra del Tigre a bordo del camión safari",
    sub: "Tours guiados en Mazamitla, Pueblo Mágico de Jalisco. Elige fecha, paga en línea y muestra tu QR al abordar.",
    buscar: "Ver horarios",
    fecha: "Fecha",
    personas: "Personas",
    experiencia: "Experiencia",
    todas: "Todas",
    destacados: "Los más pedidos",
    verTodos: "Ver todos los tours",
    como: "Así de fácil",
    pasos: [
      { t: "Elige tu salida", d: "Ves los lugares libres en tiempo real." },
      { t: "Paga en línea", d: "Tu lugar queda apartado 15 minutos mientras pagas." },
      { t: "Muestra tu QR", d: "Te llega por WhatsApp y correo. Lo escaneamos al subir." },
    ],
    resenas: "en Google",
    resenasNota: (n: number, f: string) => `${n} reseñas, consultado el ${f}`,
    alsoTitulo: "Completa tu visita",
    cabanasT: "Cabañas en el bosque",
    cabanasD: "Hospédate cerca del punto de salida.",
    barT: "Bar D' 11 a 11",
    barD: "Bebidas y platillos después del tour.",
    llegarT: "Cómo llegar",
    llegarD: "Oficinas y estacionamiento propio en el centro de Mazamitla.",
  },
  tour: {
    desde: "desde",
    porPersona: "por persona",
    duracion: "Duración",
    edadMinima: "Edad mínima",
    anos: "años",
    incluye: "Incluye",
    saber: "Antes de reservar",
    llevar: "Qué llevar",
    tarifas: "Tarifas",
    adulto: "Adulto",
    nino: "Menor",
    porConfirmar: "por confirmar",
    minutos: (m: number) => (m >= 60 ? `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ""}` : `${m} min`),
    consultar: "Consultar",
  },
  reserva: {
    titulo: "Reserva",
    fecha: "Fecha",
    hora: "Horario",
    adultos: "Adultos",
    ninos: "Menores de edad",
    libres: (n: number) => (n === 1 ? "1 lugar" : `${n} lugares`),
    lleno: "Lleno",
    camiones: (n: number) => (n > 1 ? `${n} camiones` : ""),
    total: "Total",
    nombre: "Nombre completo",
    telefono: "WhatsApp (10 dígitos)",
    correo: "Correo (opcional)",
    apartar: "Apartar y pagar",
    pagando: "Apartando…",
    prisa: (n: number) => `¡Quedan ${n} lugares! Se está llenando el camión`,
    apartado: "Lugares apartados",
    expira: "Tu apartado vence en",
    listo: "¡Listo! Ya tienes tu lugar",
    folio: "Folio",
    qr: "Muestra este QR al abordar",
    sinHorarios: "No hay salidas este día. Prueba otra fecha.",
    error: "Algo salió mal. Intenta de nuevo.",
    desglose: (a: number, n: number) => `${a} adulto${a === 1 ? "" : "s"}${n ? ` + ${n} menor${n === 1 ? "" : "es"}` : ""}`,
  },
  pie: { nosotros: "Mazamitla Sierra Tours", contacto: "Contacto", legal: "Legal", privacidad: "Aviso de privacidad", reembolso: "Cancelaciones y reembolsos", trabajo: "Trabaja con nosotros", agencias: "Acceso agencias" },
};

type Dic = typeof es;

const en: Dic = {
  meta: {
    titulo: "Mazamitla Sierra Tours · Safari truck, horseback and night tours",
    descripcion: "Book tours in Mazamitla, Jalisco online: Sierra del Tigre by safari truck, waterfalls, horseback rides, night tour and helicopter.",
  },
  nav: { tours: "Tours", cabanas: "Cabins", bar: "Bar", ayuda: "Help", reservar: "Book", idioma: "Español", menu: "Menu" },
  demo: "Mazamitla Sierra Tours · Plan your visit",
  inicio: {
    h1: "Ride the Sierra del Tigre aboard our safari truck",
    sub: "Guided tours in Mazamitla, a Magic Town in Jalisco. Pick a date, pay online and show your QR when boarding.",
    buscar: "See times",
    fecha: "Date",
    personas: "People",
    experiencia: "Experience",
    todas: "All",
    destacados: "Most popular",
    verTodos: "See all tours",
    como: "It's that easy",
    pasos: [
      { t: "Pick a departure", d: "See open seats in real time." },
      { t: "Pay online", d: "Your seats are held for 15 minutes while you pay." },
      { t: "Show your QR", d: "Sent by WhatsApp and email. We scan it when you board." },
    ],
    resenas: "on Google",
    resenasNota: (n: number, f: string) => `${n} reviews, checked ${f}`,
    alsoTitulo: "Make a day of it",
    cabanasT: "Forest cabins",
    cabanasD: "Stay close to the departure point.",
    barT: "Bar D' 11 a 11",
    barD: "Drinks and food after your tour.",
    llegarT: "Getting here",
    llegarD: "Offices and private parking in downtown Mazamitla.",
  },
  tour: {
    desde: "from",
    porPersona: "per person",
    duracion: "Duration",
    edadMinima: "Minimum age",
    anos: "years",
    incluye: "Included",
    saber: "Before you book",
    llevar: "What to bring",
    tarifas: "Rates",
    adulto: "Adult",
    nino: "Child",
    porConfirmar: "to be confirmed",
    minutos: (m: number) => (m >= 60 ? `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ""}` : `${m} min`),
    consultar: "Ask us",
  },
  reserva: {
    titulo: "Booking",
    fecha: "Date",
    hora: "Time",
    adultos: "Adults",
    ninos: "Children",
    libres: (n: number) => (n === 1 ? "1 seat" : `${n} seats`),
    lleno: "Full",
    camiones: (n: number) => (n > 1 ? `${n} trucks` : ""),
    total: "Total",
    nombre: "Full name",
    telefono: "WhatsApp (10 digits)",
    correo: "Email (optional)",
    apartar: "Hold and pay",
    pagando: "Holding…",
    prisa: (n: number) => `Only ${n} seats left! The truck is filling up`,
    apartado: "Seats on hold",
    expira: "Your hold expires in",
    listo: "Done! Your seat is booked",
    folio: "Booking ID",
    qr: "Show this QR when boarding",
    sinHorarios: "No departures this day. Try another date.",
    error: "Something went wrong. Please try again.",
    desglose: (a: number, n: number) => `${a} adult${a === 1 ? "" : "s"}${n ? ` + ${n} child${n === 1 ? "" : "ren"}` : ""}`,
  },
  pie: { nosotros: "Mazamitla Sierra Tours", contacto: "Contact", legal: "Legal", privacidad: "Privacy notice", reembolso: "Cancellations and refunds", trabajo: "Work with us", agencias: "Agency login" },
};

export const diccionario = (idioma: Idioma): Dic => (idioma === "en" ? en : es);
export type Diccionario = Dic;
